import React from 'react';
import {useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import {isSafeRelativeTarget} from '@utils/urlValidation';
import MicrosoftIcon from '../../../shared/icons/sso/Microsoft';
import SurfIcon from '../../../shared/icons/sso/SurfIcon';
import OktaIcon from '../../../shared/icons/sso/OktaIcon';
import AzureIcon from '../../../shared/icons/sso/Azure';
import UtrechtUniversityIcon from '../../../shared/icons/sso/UtrechtUniversity';
import UnknownIdpIcon from '../../../shared/icons/sso/UnknownIdpIcon';
import {selectFederatedIdentities} from '../../userSlice';
import './ssoForm.less';

const PROVIDER_TYPE_TO_ICON = {
    azure: <AzureIcon className="sso-icon" />,
    adfs: <MicrosoftIcon className="sso-icon" />,
    okta: <OktaIcon className="sso-icon" />,
    surf: <SurfIcon className="sso-icon" />,
    utrecht: <UtrechtUniversityIcon className="sso-icon" />
};

const PROVIDER_TYPE_TO_NAME = {
    azure: 'Microsoft',
    adfs: 'Microsoft',
    okta: 'Okta',
    surf: 'SURFconext',
    utrecht: 'Utrecht University'
};

function getSsoUrlWithTarget(ssoLoginUrl, target) {
    const safeTarget = isSafeRelativeTarget(target);
    if (!safeTarget) return ssoLoginUrl;
    const separator = ssoLoginUrl.includes('?') ? '&' : '?';
    return `${ssoLoginUrl}${separator}target=${encodeURIComponent(safeTarget)}`;
}

export default function SsoForm({wasSsoFetchedForEmail}) {
    const intl = useIntl();
    const federatedIdentities = useSelector(selectFederatedIdentities);
    const [searchParams] = useSearchParams();
    const targetParam = searchParams.get('target');
    const hasSso = federatedIdentities.length > 0;

    function getProviderIcon(providerType) {
        return PROVIDER_TYPE_TO_ICON[providerType] || <UnknownIdpIcon className="sso-icon" />;
    }

    function getProviderName(providerType) {
        return PROVIDER_TYPE_TO_NAME[providerType]
            || intl.formatMessage({id: 'esl.generic.sso', defaultMessage: 'SSO'});
    }

    function renderSsoButton(identity) {
        const {identityProvider, ssoLoginUrl, providerType} = identity;
        const ssoHref = getSsoUrlWithTarget(ssoLoginUrl, targetParam);

        return (
            <AppOutlinedButton
                href={ssoHref}
                className="sso-login-btn"
                startIcon={getProviderIcon(providerType)}
                key={identityProvider}
                fullWidth
            >
                <div className="sso-name">
                    {intl.formatMessage({id: 'page.login.sso.loginWith', defaultMessage: ''}, {
                        name: getProviderName(providerType)
                    })}
                </div>
            </AppOutlinedButton>
        );
    }

    if (!hasSso || !wasSsoFetchedForEmail) {
        return null;
    }

    return (
        <div className="login-sso-form">
            <div className="title-block">
                <h2 className="sso-form-title">
                    {intl.formatMessage({id: 'page.login.sso.title', defaultMessage: 'Sign in with SSO'})}
                </h2>
                <p className="sso-form-subtitle">
                    {intl.formatMessage({id: 'page.login.sso.subtitle', defaultMessage: 'Choose your identity provider to continue'})}
                </p>
            </div>
            <div className="sso-buttons-container">
                {federatedIdentities.map(renderSsoButton)}
            </div>
        </div>
    );
}
